import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Activity, ArrowDown, ArrowUp, Plus, Trash2, Search, User, Settings, FileText, Ticket, Mail } from "lucide-react";
import { API } from '../utils/constants';
import UserTypeSelect from './UserTypeSelect';

const OwnerDashboard = ({ open, onOpenChange }) => {
  const [activeTab, setActiveTab] = useState('beta');
  const [betaSubmissions, setBetaSubmissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loadingWaitlist, setLoadingWaitlist] = useState(false);
  const [waitlistError, setWaitlistError] = useState(false);
  const [savingUserTypes, setSavingUserTypes] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [userSort, setUserSort] = useState('newest');
  const [loading, setLoading] = useState(false);
  const [invites, setInvites] = useState([]);
  const [generatingInviteFor, setGeneratingInviteFor] = useState(null);
  const [shareCodes, setShareCodes] = useState([]);
  const [newShareCode, setNewShareCode] = useState({ code: '', max_uses: '', expires_at: '' });
  const [creatingShareCode, setCreatingShareCode] = useState(false);
  const [togglingShareCode, setTogglingShareCode] = useState(null);
  const [showAddBetaUser, setShowAddBetaUser] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, submission: null });
  const [defaultCategories, setDefaultCategories] = useState([]);
  const [defaultCategoriesVersion, setDefaultCategoriesVersion] = useState(null);
  const [loadingDefaultCategories, setLoadingDefaultCategories] = useState(false);
  const [savingDefaultCategories, setSavingDefaultCategories] = useState(false);
  const [welcomeBackfill, setWelcomeBackfill] = useState(null);
  const [checkingWelcomeBackfill, setCheckingWelcomeBackfill] = useState(false);
  const [sendingWelcomeBackfill, setSendingWelcomeBackfill] = useState(false);
  const [emailActivity, setEmailActivity] = useState({
    emails: [],
    totals: { sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, complained: 0 }
  });
  const [loadingEmailActivity, setLoadingEmailActivity] = useState(false);
  const [newBetaUser, setNewBetaUser] = useState({
    email: '',
    first_name: '',
    last_name: '',
    category: 'Friend',
    notes: ''
  });

  // The Users tab label is visible as soon as the dashboard opens, so load
  // its count independently of whichever content tab happens to be active.
  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (activeTab === 'beta') {
        fetchBetaTabData();
      } else if (activeTab === 'shareCodes') {
        fetchShareCodes();
      } else if (activeTab === 'emailActivity') {
        fetchEmailActivity();
      } else if (activeTab === 'settings') {
        fetchDefaultCategories();
      } else if (activeTab === 'waitlist') {
        fetchWaitlist();
      }
    }
  }, [open, activeTab]);

  const fetchWaitlist = async () => {
    setLoadingWaitlist(true);
    setWaitlistError(false);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/waitlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWaitlist(response.data);
    } catch (error) {
      setWaitlistError(true);
      toast.error(error.response?.data?.detail || "Failed to load waitlist");
    } finally {
      setLoadingWaitlist(false);
    }
  };

  const handleUpdateUserType = async (resource, row, user_type) => {
    const key = `${resource}:${resource === 'waitlist' ? row.email : row.id}`;
    if (savingUserTypes[key] || (row.user_type || 'early_access') === user_type) return;
    setSavingUserTypes((current) => ({ ...current, [key]: true }));
    try {
      const token = localStorage.getItem('adminToken');
      const path = resource === 'beta'
        ? `/beta-submissions/${encodeURIComponent(row.id)}`
        : resource === 'users'
          ? `/users/${encodeURIComponent(row.id)}/user-type`
          : '/waitlist/user-type';
      await axios.patch(
        `${API}${path}`,
        resource === 'waitlist' ? { email: row.email, user_type } : { user_type },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const setRows = resource === 'beta' ? setBetaSubmissions : resource === 'users' ? setUsers : setWaitlist;
      setRows((current) => current.map((entry) => (
        (resource === 'waitlist' ? entry.email === row.email : entry.id === row.id)
          ? { ...entry, user_type } : entry
      )));
      toast.success("User type updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user type");
    } finally {
      setSavingUserTypes((current) => ({ ...current, [key]: false }));
    }
  };

  const fetchDefaultCategories = async () => {
    setLoadingDefaultCategories(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/settings/default-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefaultCategories(response.data.categories || []);
      setDefaultCategoriesVersion(response.data.version);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load default categories");
    } finally {
      setLoadingDefaultCategories(false);
    }
  };

  const requestWelcomeBackfill = async (dryRun) => {
    const setBusy = dryRun ? setCheckingWelcomeBackfill : setSendingWelcomeBackfill;
    setBusy(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API}/admin/lifecycle/welcome-backfill`,
        { dry_run: dryRun, batch_size: 25 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWelcomeBackfill(response.data);
      if (!dryRun) {
        toast.success(
          response.data.scheduled_count
            ? `Started the Welcome sequence for ${response.data.scheduled_count} account${response.data.scheduled_count === 1 ? '' : 's'}`
            : "No eligible accounts remain"
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check Welcome email eligibility");
    } finally {
      setBusy(false);
    }
  };

  const fetchEmailActivity = async () => {
    setLoadingEmailActivity(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/lifecycle/email-activity?limit=250`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailActivity(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load email activity");
    } finally {
      setLoadingEmailActivity(false);
    }
  };

  const updateDefaultCategoryName = (index, name) => {
    setDefaultCategories((current) => current.map(
      (category, categoryIndex) => (
        categoryIndex === index ? { ...category, name } : category
      )
    ));
  };

  const addDefaultCategory = () => {
    const id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `default-${Date.now()}`;
    setDefaultCategories((current) => [
      ...current,
      { id, name: '', order: current.length },
    ]);
  };

  const removeDefaultCategory = (index) => {
    if (defaultCategories.length <= 1) {
      toast.error("At least one default category is required");
      return;
    }
    setDefaultCategories((current) => current.filter((_, categoryIndex) => (
      categoryIndex !== index
    )));
  };

  const moveDefaultCategory = (index, direction) => {
    setDefaultCategories((current) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.length) return current;
      const reordered = [...current];
      [reordered[index], reordered[targetIndex]] = [
        reordered[targetIndex],
        reordered[index],
      ];
      return reordered;
    });
  };

  const saveDefaultCategories = async () => {
    const categories = defaultCategories.map((category, order) => ({
      ...category,
      name: (category.name || '').trim(),
      order,
    }));
    if (categories.length === 0) {
      toast.error("At least one default category is required");
      return;
    }
    if (categories.some((category) => !category.name)) {
      toast.error("Default category names cannot be blank");
      return;
    }
    setSavingDefaultCategories(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(
        `${API}/settings/default-categories`,
        { categories, version: defaultCategoriesVersion },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDefaultCategories(response.data.categories || []);
      setDefaultCategoriesVersion(response.data.version);
      toast.success("Default categories updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update default categories");
      if (error.response?.status === 409) {
        await fetchDefaultCategories();
      }
    } finally {
      setSavingDefaultCategories(false);
    }
  };

  const fetchShareCodes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      const resp = await axios.get(`${API}/share-codes`, { headers });
      setShareCodes(resp.data.share_codes || []);
    } catch (error) {
      console.error("Failed to fetch shareable codes:", error);
      toast.error("Failed to load shareable invite codes");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShareCode = async () => {
    const code = (newShareCode.code || '').trim();
    const maxUses = parseInt(newShareCode.max_uses, 10);
    if (!code) {
      toast.error("Enter a code (e.g. BRITTANY5)");
      return;
    }
    if (!Number.isFinite(maxUses) || maxUses < 1) {
      toast.error("Enter a maximum number of uses (at least 1)");
      return;
    }
    setCreatingShareCode(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        code,
        max_uses: maxUses,
        expires_at: newShareCode.expires_at
          ? new Date(`${newShareCode.expires_at}T23:59:59`).toISOString()
          : null,
      };
      await axios.post(`${API}/share-codes`, payload, { headers });
      toast.success(`Code ${code.toUpperCase()} created`);
      setNewShareCode({ code: '', max_uses: '', expires_at: '' });
      await fetchShareCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create code");
    } finally {
      setCreatingShareCode(false);
    }
  };

  const handleToggleShareCode = async (code, active) => {
    setTogglingShareCode(code);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/share-codes/${encodeURIComponent(code)}`, { active }, { headers });
      toast.success(active ? `Code ${code} activated` : `Code ${code} deactivated`);
      await fetchShareCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update code");
    } finally {
      setTogglingShareCode(null);
    }
  };

  const fetchInvites = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      const invitesResp = await axios.get(`${API}/invites`, { headers });
      setInvites(invitesResp.data.invites || []);
    } catch (error) {
      console.error("Failed to fetch invites:", error);
      toast.error("Failed to load invite codes");
    }
  };

  const fetchBetaTabData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      const [submissionsResp, invitesResp] = await Promise.all([
        axios.get(`${API}/beta-submissions`, { headers }),
        axios.get(`${API}/invites`, { headers }),
      ]);
      setBetaSubmissions(submissionsResp.data);
      setInvites(invitesResp.data.invites || []);
    } catch (error) {
      toast.error("Failed to fetch beta submissions");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBetaSubmissions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/beta-submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBetaSubmissions(response.data);
    } catch (error) {
      toast.error("Failed to fetch beta submissions");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/all-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBetaSubmission = async () => {
    const { submission } = deleteConfirmDialog;
    if (!submission) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/beta-submissions/${submission.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Beta submission deleted successfully");
      setDeleteConfirmDialog({ open: false, submission: null });
      fetchBetaSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete beta submission");
      console.error(error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleTogglePaywall = async (userId, nextDisabled) => {
    // Optimistic flip so the switch responds instantly; we revert on error.
    const prev = users;
    setUsers(prev.map((u) => (u.id === userId ? { ...u, paywall_disabled: nextDisabled } : u)));
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${API}/users/${userId}/paywall`,
        { paywall_disabled: nextDisabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(nextDisabled ? "Paywall disabled for user" : "Paywall re-enabled for user");
    } catch (error) {
      setUsers(prev);
      toast.error(error.response?.data?.detail || "Failed to update paywall");
    }
  };

  const handleSendInvite = async (invitee_email) => {
    if (!invitee_email) {
      toast.error("Beta user is missing an email address");
      return;
    }
    setGeneratingInviteFor(invitee_email.toLowerCase());
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API}/invites/send`,
        { invitee_email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data?.message || `Invitation emailed to ${invitee_email}`);
      await fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setGeneratingInviteFor(null);
    }
  };

  const handleRevokeInvite = async (code) => {
    if (!window.confirm(`Revoke invite ${code}? It will no longer work for sign-up.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/invites/${encodeURIComponent(code)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invite ${code} revoked`);
      await fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke invite code");
    }
  };

  const inviteStatus = (inv) => {
    if (inv.code?.startsWith('ref-')) return inv.status;
    if (inv.used_at) return 'used';
    if (inv.expires_at && new Date(inv.expires_at) < new Date()) return 'expired';
    return 'active';
  };

  const handleAddManualBetaUser = async () => {
    if (!newBetaUser.email || !newBetaUser.first_name) {
      toast.error("Email and first name are required");
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/beta-submissions/manual`, newBetaUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Beta user added successfully!");
      setShowAddBetaUser(false);
      setNewBetaUser({
        email: '',
        first_name: '',
        last_name: '',
        category: 'Friend',
        notes: ''
      });
      fetchBetaSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add beta user");
    }
  };

  const handleUpdateBetaUserCategory = async (submissionId, category) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API}/beta-submissions/${submissionId}`, 
        { category },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setBetaSubmissions((current) => current.map(sub =>
        sub.id === submissionId ? { ...sub, category } : sub
      ));
      toast.success("Category updated!");
    } catch (error) {
      toast.error("Failed to update category");
    }
  };

  const handleUpdateBetaUserNotes = async (submissionId, notes) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API}/beta-submissions/${submissionId}`, 
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setBetaSubmissions((current) => current.map(sub =>
        sub.id === submissionId ? { ...sub, notes } : sub
      ));
      toast.success("Notes updated!");
    } catch (error) {
      toast.error("Failed to update notes");
    }
  };

  const filteredBetaSubmissions = betaSubmissions.filter(sub => 
    sub.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sub.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sub.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Map: lowercased invitee_email → most-relevant invite for that user.
  // Prefer active over expired over used so the per-row UI surfaces the
  // useful state first (an active code beats an old used one).
  const invitesByEmail = (() => {
    const rank = { active: 0, expired: 1, used: 2 };
    const map = new Map();
    for (const inv of invites) {
      const key = (inv.invitee_email || '').toLowerCase();
      if (!key) continue;
      const existing = map.get(key);
      if (!existing || rank[inviteStatus(inv)] < rank[inviteStatus(existing)]) {
        map.set(key, inv);
      }
    }
    return map;
  })();

  const filteredUsers = users.filter(user =>
    user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const USER_SORTS = {
    newest: { label: 'Newest first', fn: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0) },
    last_active: { label: 'Last active', fn: (a, b) => new Date(b.last_active_at || 0) - new Date(a.last_active_at || 0) },
    last_login: { label: 'Last login', fn: (a, b) => new Date(b.last_login_at || 0) - new Date(a.last_login_at || 0) },
    recipes: { label: 'Most recipes', fn: (a, b) => (b.recipe_count || 0) - (a.recipe_count || 0) },
    logins: { label: 'Most logins', fn: (a, b) => (b.login_count || 0) - (a.login_count || 0) },
    shares: { label: 'Most shares', fn: (a, b) => (b.share_clicks || 0) - (a.share_clicks || 0) },
    views: { label: 'Most page views', fn: (a, b) => (b.page_views || 0) - (a.page_views || 0) },
  };
  const sortedUsers = [...filteredUsers].sort(USER_SORTS[userSort]?.fn || USER_SORTS.newest.fn);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const lifecycleEmailName = (step) => ({
    'create-account-invite-welcome-0': 'Create Account invitation',
    'create-account-reminder-1': 'Create Account · Day 2',
    'create-account-reminder-2': 'Create Account · Day 5',
    'create-account-reminder-3': 'Create Account · Day 10',
    'create-account-reminder-4': 'Create Account · Day 21',
    'Welcome-my-story-1': 'Welcome · My Story',
    'welcome-reminder-1': 'Welcome · Day 1',
    'welcome-reminder-2': 'Welcome · Day 3',
    'welcome-reminder-3': 'Welcome · Day 7',
    'welcome-reminder-4': 'Welcome · Day 14',
    'welcome-reminder-5': 'Welcome · Day 21',
    'welcome-reminder-6': 'Welcome · Day 30',
  }[step] || step);

  const lifecycleEmailStatus = (email) => {
    if (email.complained_at) return { label: 'Complained', color: '#A33A32' };
    if (email.bounced_at) return { label: 'Bounced', color: '#A33A32' };
    if (email.first_clicked_at) return { label: 'Clicked', color: '#3F8A52' };
    if (email.first_opened_at) return { label: 'Opened', color: '#3F8A52' };
    if (email.delivered_at) return { label: 'Delivered', color: '#526B87' };
    return { label: 'Sent', color: '#875C66' };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        style={{ 
          backgroundColor: '#F3EEE4', 
          border: '1px solid #D6D0C2',
          maxWidth: '1000px'
        }}
        className="flex flex-col"
      >
        <DialogHeader>
          <DialogTitle 
            style={{ 
              fontFamily: "'Instrument Serif', serif", 
              fontSize: '28px',
              color: '#51120A' 
            }}
          >
            Owner Dashboard
          </DialogTitle>
        </DialogHeader>
        
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b pb-2" style={{ borderColor: '#D6D0C2' }}>
          <button
            onClick={() => setActiveTab('beta')}
            className={`px-4 py-2 rounded-t transition-colors flex items-center gap-2 ${
              activeTab === 'beta' ? 'bg-[#C9D7E9]' : 'hover:bg-[#EEE7D8]'
            }`}
            style={{ 
              fontFamily: "'Bricolage Grotesque', sans-serif",
              color: '#51120A',
              border: 'none'
            }}
          >
            <FileText size={16} />
            Beta Submissions ({betaSubmissions.length})
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-t transition-colors flex items-center gap-2 ${
              activeTab === 'users' ? 'bg-[#C9D7E9]' : 'hover:bg-[#EEE7D8]'
            }`}
            style={{ 
              fontFamily: "'Bricolage Grotesque', sans-serif",
              color: '#51120A',
              border: 'none'
            }}
          >
            <User size={16} />
            Users ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('waitlist')}
            className={`px-4 py-2 rounded-t transition-colors flex items-center gap-2 shrink-0 ${
              activeTab === 'waitlist' ? 'bg-[#C9D7E9]' : 'hover:bg-[#EEE7D8]'
            }`}
            style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              color: '#51120A',
              border: 'none',
            }}
          >
            <Mail size={16} />
            Waitlist
          </button>
          <button
            onClick={() => setActiveTab('shareCodes')}
            className={`px-4 py-2 rounded-t transition-colors flex items-center gap-2 ${
              activeTab === 'shareCodes' ? 'bg-[#C9D7E9]' : 'hover:bg-[#EEE7D8]'
            }`}
            style={{ 
              fontFamily: "'Bricolage Grotesque', sans-serif",
              color: '#51120A',
              border: 'none'
            }}
          >
            <Ticket size={16} />
            Shareable Codes
          </button>
          <button
            onClick={() => setActiveTab('emailActivity')}
            className={`px-4 py-2 rounded-t transition-colors flex items-center gap-2 shrink-0 ${
              activeTab === 'emailActivity' ? 'bg-[#C9D7E9]' : 'hover:bg-[#EEE7D8]'
            }`}
            style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              color: '#51120A',
              border: 'none'
            }}
          >
            <Activity size={16} />
            Email Activity
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 rounded-t transition-colors flex items-center gap-2 ${
              activeTab === 'settings' ? 'bg-[#C9D7E9]' : 'hover:bg-[#EEE7D8]'
            }`}
            style={{ 
              fontFamily: "'Bricolage Grotesque', sans-serif",
              color: '#51120A',
              border: 'none'
            }}
          >
            <Settings size={16} />
            Settings
          </button>
        </div>

        {/* Search Bar (for beta and users tabs) */}
        {(activeTab === 'beta' || activeTab === 'users') && (
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: '#51120A', opacity: 0.5 }} />
              <Input
                placeholder={`Search ${activeTab === 'beta' ? 'submissions' : 'users'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                style={{ 
                  borderColor: '#D6D0C2', 
                  boxShadow: 'none',
                  backgroundColor: 'transparent',
                  fontFamily: "'Bricolage Grotesque', sans-serif"
                }}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="mt-4">
          {loading ? (
            <div className="text-center py-8" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              Loading...
            </div>
          ) : (
            <>
              {/* Beta Submissions Tab */}
              {activeTab === 'beta' && (
                <div className="space-y-3">
                  {/* Add Manual Entry Button */}
                  <div className="flex justify-end mb-4">
                    <Button
                      onClick={() => setShowAddBetaUser(true)}
                      style={{ 
                        backgroundColor: '#51120A',
                        fontFamily: "'Bricolage Grotesque', sans-serif"
                      }}
                    >
                      + Add Manual Entry
                    </Button>
                  </div>

                  {filteredBetaSubmissions.length === 0 ? (
                    <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                      No beta submissions yet.
                    </p>
                  ) : (
                    filteredBetaSubmissions.map((submission) => (
                      <div
                        key={submission.id}
                        className="p-4 rounded-lg border hover:border-[#AE8890] transition-colors"
                        style={{ 
                          backgroundColor: 'transparent',
                          borderColor: '#D6D0C2'
                        }}
                      >
                        <div className="flex flex-wrap gap-3 justify-between items-start mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 
                              className="font-semibold text-lg"
                              style={{ 
                                color: '#51120A',
                                fontFamily: "'Instrument Serif', serif"
                              }}
                            >
                              {submission.first_name} {submission.last_name}
                            </h3>
                            <p 
                              className="text-sm"
                              style={{ 
                                color: '#875C66',
                                fontFamily: "'Bricolage Grotesque', sans-serif"
                              }}
                            >
                              {submission.email}
                            </p>
                            
                            {/* Category Dropdown */}
                            <UserTypeSelect
                              contact={submission.email || submission.id}
                              value={submission.user_type}
                              saving={!!savingUserTypes[`beta:${submission.id}`]}
                              onChange={(value) => handleUpdateUserType('beta', submission, value)}
                            />
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <label className="text-xs" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                                Category:
                              </label>
                              <select
                                value={submission.category || 'Friend'}
                                onChange={(e) => handleUpdateBetaUserCategory(submission.id, e.target.value)}
                                className="text-xs px-2 py-1 rounded"
                                style={{ 
                                  backgroundColor: submission.category === 'Friend' ? '#C9D7E9' : '#EEE7D8',
                                  border: '1px solid #D6D0C2',
                                  color: '#51120A',
                                  fontFamily: "'Bricolage Grotesque', sans-serif"
                                }}
                              >
                                <option value="Friend">Friend</option>
                                <option value="Cold Lead">Cold Lead</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span 
                              className="text-xs"
                              style={{ 
                                color: '#51120A',
                                opacity: 0.7,
                                fontFamily: "'Bricolage Grotesque', sans-serif"
                              }}
                            >
                              {formatDate(submission.created_at)}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirmDialog({ open: true, submission })}
                              className="hover:bg-red-100"
                              style={{ color: '#AE8890', padding: '4px 8px' }}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                        
                        <div 
                          className="grid grid-cols-2 gap-2 text-sm mt-3"
                          style={{ 
                            color: '#51120A',
                            fontFamily: "'Bricolage Grotesque', sans-serif"
                          }}
                        >
                          <div>
                            <strong>Identity:</strong> {submission.identity}
                          </div>
                          <div>
                            <strong>Recipe Tracking:</strong> {submission.recipe_tracking}
                          </div>
                          {submission.platforms && (
                            <div className="col-span-2">
                              <strong>Platforms:</strong> {submission.platforms.join(', ')}
                            </div>
                          )}
                          {submission.beta_excitement && (
                            <div className="col-span-2">
                              <strong>Excitement:</strong> {submission.beta_excitement}
                            </div>
                          )}
                        </div>

                        {/* Notes Field */}
                        <div className="mt-3">
                          <label className="text-sm font-semibold" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                            Notes:
                          </label>
                          <textarea
                            value={submission.notes || ''}
                            onChange={(e) => {
                              // Update local state immediately for better UX
                              setBetaSubmissions(betaSubmissions.map(sub =>
                                sub.id === submission.id ? { ...sub, notes: e.target.value } : sub
                              ));
                            }}
                            onBlur={(e) => handleUpdateBetaUserNotes(submission.id, e.target.value)}
                            placeholder="Add notes about this person..."
                            className="w-full mt-1 p-2 rounded text-sm"
                            style={{ 
                              backgroundColor: '#EEE7D8',
                              border: '1px solid #D6D0C2',
                              color: '#51120A',
                              fontFamily: "'Bricolage Grotesque', sans-serif",
                              minHeight: '60px'
                            }}
                          />
                        </div>

                        {/* Email invitation */}
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: '#D6D0C2' }}>
                          <label
                            className="text-sm font-semibold"
                            style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                          >
                            Invitation:
                          </label>
                          {(() => {
                            const submissionEmail = (submission.email || '').toLowerCase();
                            const inv = submissionEmail ? invitesByEmail.get(submissionEmail) : null;
                            const isSending = generatingInviteFor === submissionEmail;
                            if (inv) {
                              const status = inviteStatus(inv);
                              const statusColor =
                                status === 'active' ? '#3F8A52' :
                                status === 'used' ? '#875C66' :
                                '#AE8890';
                              return (
                                <div
                                  className="mt-1 p-2 rounded flex items-center justify-between gap-2 flex-wrap"
                                  style={{ backgroundColor: '#EEE7D8', border: '1px solid #D6D0C2' }}
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <Mail size={14} style={{ color: statusColor }} />
                                      <span
                                        className="text-xs uppercase font-semibold"
                                        style={{ color: statusColor, fontFamily: "'Bricolage Grotesque', sans-serif" }}
                                      >
                                        {inv.code?.startsWith('ref-')
                                          ? (status === 'active' ? 'Loops enrollment accepted' : status === 'used' ? 'Joined' : 'Invitation stopped')
                                          : status === 'active' ? 'Invite sent' : status === 'used' ? 'Joined' : 'Invite expired'}
                                      </span>
                                    </div>
                                    <div
                                      className="text-xs mt-1"
                                      style={{ color: '#51120A', opacity: 0.7, fontFamily: "'Bricolage Grotesque', sans-serif" }}
                                    >
                                      {status === 'active' && (inv.code?.startsWith('ref-')
                                        ? 'Stable link · inbox verification required'
                                        : `Link expires ${formatDate(inv.expires_at)}`)}
                                      {status === 'expired' && `Expired ${formatDate(inv.expires_at)}`}
                                      {status === 'used' && `Account created ${formatDate(inv.used_at)}`}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {status !== 'used' && (
                                      <Button
                                        size="sm"
                                        onClick={() => handleSendInvite(submission.email)}
                                        disabled={isSending || !submission.email}
                                        data-testid={`resend-invite-${submissionEmail}`}
                                        style={{
                                          backgroundColor: '#51120A',
                                          fontFamily: "'Bricolage Grotesque', sans-serif",
                                          padding: '4px 10px',
                                          fontSize: '12px',
                                        }}
                                      >
                                        {isSending ? 'Sending...' : 'Resend invite'}
                                      </Button>
                                    )}
                                    {status === 'active' && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRevokeInvite(inv.code)}
                                        title="Revoke invitation"
                                        className="hover:bg-red-100"
                                        style={{ color: '#AE8890', padding: '4px 8px' }}
                                      >
                                        <Trash2 size={16} />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div className="mt-1 flex items-center justify-between gap-2 flex-wrap">
                                <span
                                  className="text-sm"
                                  style={{ color: '#51120A', opacity: 0.7, fontFamily: "'Bricolage Grotesque', sans-serif" }}
                                >
                                  No invitation sent yet. Invitation links expire after 48 hours.
                                </span>
                                <Button
                                  size="sm"
                                  onClick={() => handleSendInvite(submission.email)}
                                  disabled={isSending || !submission.email}
                                  data-testid={`send-invite-${submissionEmail}`}
                                  style={{
                                    backgroundColor: '#51120A',
                                    fontFamily: "'Bricolage Grotesque', sans-serif",
                                    padding: '4px 10px',
                                    fontSize: '12px',
                                  }}
                                >
                                  {isSending ? 'Sending...' : 'Send invite'}
                                </Button>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 justify-end">
                    <Label
                      htmlFor="user-sort"
                      className="text-xs"
                      style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                    >
                      Sort by
                    </Label>
                    <select
                      id="user-sort"
                      data-testid="user-sort"
                      value={userSort}
                      onChange={(e) => setUserSort(e.target.value)}
                      className="text-sm rounded-md border px-2 py-1"
                      style={{
                        borderColor: '#D6D0C2',
                        color: '#51120A',
                        backgroundColor: 'transparent',
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                      }}
                    >
                      {Object.entries(USER_SORTS).map(([key, s]) => (
                        <option key={key} value={key}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  {sortedUsers.length === 0 ? (
                    <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                      No users found.
                    </p>
                  ) : (
                    sortedUsers.map((user) => (
                      <div
                        key={user.id}
                        className="p-4 rounded-lg border hover:border-[#AE8890] transition-colors flex flex-wrap gap-3 justify-between items-center"
                        style={{ 
                          backgroundColor: 'transparent',
                          borderColor: '#D6D0C2'
                        }}
                      >
                        <div>
                          <h3 
                            className="font-semibold"
                            style={{ 
                              color: '#51120A',
                              fontFamily: "'Instrument Serif', serif"
                            }}
                          >
                            {user.first_name || 'Unknown'}
                          </h3>
                          <p 
                            className="text-sm"
                            style={{ 
                              color: '#875C66',
                              fontFamily: "'Bricolage Grotesque', sans-serif"
                            }}
                          >
                            {user.email}
                          </p>
                          {user.username && (
                            <p
                              className="text-sm"
                              style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}
                            >
                              <a
                                href={`/${user.username}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:opacity-70"
                                style={{ color: '#51120A' }}
                                data-testid={`user-page-link-${user.username}`}
                              >
                                @{user.username}
                              </a>
                            </p>
                          )}
                          <UserTypeSelect
                            contact={user.email || user.id}
                            value={user.user_type}
                            saving={!!savingUserTypes[`users:${user.id}`]}
                            onChange={(value) => handleUpdateUserType('users', user, value)}
                          />
                          <p 
                            className="text-xs mt-1"
                            style={{ 
                              color: '#51120A',
                              opacity: 0.6,
                              fontFamily: "'Bricolage Grotesque', sans-serif"
                            }}
                          >
                            Joined: {formatDate(user.created_at)}
                          </p>
                          <p
                            className="text-xs mt-1"
                            style={{
                              color: '#51120A',
                              opacity: 0.75,
                              fontFamily: "'Bricolage Grotesque', sans-serif"
                            }}
                          >
                            {(user.recipe_count || 0)} recipes · {(user.login_count || 0)} logins · {(user.share_clicks || 0)} shares · {(user.page_views || 0)} page views
                          </p>
                          <p
                            className="text-xs mt-0.5"
                            style={{
                              color: '#51120A',
                              opacity: 0.6,
                              fontFamily: "'Bricolage Grotesque', sans-serif"
                            }}
                          >
                            Last login: {user.last_login_at ? formatDate(user.last_login_at) : 'Not yet tracked'} · Last active: {user.last_active_at ? formatDate(user.last_active_at) : 'Not yet tracked'}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {!user.is_owner && (
                            <div className="flex items-center gap-2">
                              <Label
                                htmlFor={`paywall-${user.id}`}
                                className="text-xs cursor-pointer select-none"
                                style={{
                                  color: '#51120A',
                                  fontFamily: "'Bricolage Grotesque', sans-serif",
                                }}
                                title="When ON, this user bypasses the paywall (comp / beta access)."
                              >
                                Paywall {user.paywall_disabled ? 'OFF' : 'ON'}
                              </Label>
                              <Switch
                                id={`paywall-${user.id}`}
                                checked={!!user.paywall_disabled}
                                onCheckedChange={(checked) => handleTogglePaywall(user.id, checked)}
                                aria-label={`Toggle paywall for ${user.email}`}
                                className="data-[state=checked]:bg-gray-300 data-[state=unchecked]:bg-green-600"
                              />
                            </div>
                          )}

                          {!user.is_owner && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id)}
                              className="hover:bg-red-100"
                              style={{ color: '#AE8890' }}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Shareable Codes Tab */}
              {activeTab === 'waitlist' && (
                <div className="space-y-3" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  <h3 className="text-lg" style={{ fontFamily: "'Instrument Serif', serif" }}>Waitlist contacts</h3>
                  {loadingWaitlist ? (
                    <div role="status" aria-label="Loading waitlist" className="space-y-3">
                      {[0, 1, 2].map((key) => <div key={key} className="h-20 rounded-lg" style={{ backgroundColor: '#EEE7D8' }} />)}
                    </div>
                  ) : waitlistError ? (
                    <div role="alert" className="space-y-2">
                      <p>Unable to load waitlist contacts.</p>
                      <Button onClick={fetchWaitlist} style={{ backgroundColor: '#51120A' }}>Retry</Button>
                    </div>
                  ) : waitlist.length === 0 ? (
                    <p className="p-4 rounded-lg border" style={{ borderColor: '#D6D0C2' }}>No waitlist contacts yet.</p>
                  ) : waitlist.map((contact) => (
                    <div key={contact.email} className="p-4 rounded-lg border flex flex-wrap items-center justify-between gap-3" style={{ borderColor: '#D6D0C2' }}>
                      <div className="min-w-0">
                        <h4 className="font-semibold" style={{ fontFamily: "'Instrument Serif', serif" }}>
                          {contact.name || [contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Unknown'}
                        </h4>
                        <p className="text-sm break-all" style={{ color: '#875C66' }}>{contact.email}</p>
                      </div>
                      <UserTypeSelect
                        contact={contact.email}
                        value={contact.user_type}
                        saving={!!savingUserTypes[`waitlist:${contact.email}`]}
                        onChange={(value) => handleUpdateUserType('waitlist', contact, value)}
                      />
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'shareCodes' && (
                <div className="space-y-6">
                  <div>
                    <h3
                      className="text-lg font-semibold mb-2"
                      style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }}
                    >
                      Create a Shareable Code
                    </h3>
                    <p
                      className="text-sm mb-4"
                      style={{ color: '#51120A', opacity: 0.7, fontFamily: "'Bricolage Grotesque', sans-serif" }}
                    >
                      For DMs, social media, or in-person invites. Not tied to an email — anyone
                      with the code can sign up until it hits its usage limit, expires, or is deactivated.
                    </p>
                    <div className="flex items-end gap-3 flex-wrap">
                      <div>
                        <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                          Code
                        </Label>
                        <Input
                          value={newShareCode.code}
                          onChange={(e) => setNewShareCode({ ...newShareCode, code: e.target.value.toUpperCase() })}
                          placeholder="e.g. BRITTANY5"
                          data-testid="sharecode-code-input"
                          style={{
                            borderColor: '#D6D0C2',
                            backgroundColor: 'transparent',
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                            width: '180px',
                          }}
                        />
                      </div>
                      <div>
                        <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                          Max uses
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          value={newShareCode.max_uses}
                          onChange={(e) => setNewShareCode({ ...newShareCode, max_uses: e.target.value })}
                          placeholder="5"
                          data-testid="sharecode-maxuses-input"
                          style={{
                            borderColor: '#D6D0C2',
                            backgroundColor: 'transparent',
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                            width: '110px',
                          }}
                        />
                      </div>
                      <div>
                        <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                          Expires (optional)
                        </Label>
                        <Input
                          type="date"
                          value={newShareCode.expires_at}
                          onChange={(e) => setNewShareCode({ ...newShareCode, expires_at: e.target.value })}
                          data-testid="sharecode-expires-input"
                          style={{
                            borderColor: '#D6D0C2',
                            backgroundColor: 'transparent',
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                            width: '170px',
                          }}
                        />
                      </div>
                      <Button
                        onClick={handleCreateShareCode}
                        disabled={creatingShareCode}
                        data-testid="sharecode-create-button"
                        style={{ backgroundColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                      >
                        {creatingShareCode ? 'Creating...' : 'Create Code'}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <h3
                      className="text-lg font-semibold mb-2"
                      style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }}
                    >
                      Code History
                    </h3>
                    {shareCodes.length === 0 ? (
                      <p style={{ color: '#51120A', opacity: 0.7, fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                        No shareable codes yet.
                      </p>
                    ) : (
                      <div className="overflow-x-auto rounded" style={{ border: '1px solid #D6D0C2' }}>
                        <table className="w-full text-sm" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }} data-testid="sharecode-table">
                          <thead>
                            <tr style={{ backgroundColor: '#EEE7D8', textAlign: 'left' }}>
                              <th className="px-3 py-2">Code</th>
                              <th className="px-3 py-2">Status</th>
                              <th className="px-3 py-2">Uses</th>
                              <th className="px-3 py-2">Created</th>
                              <th className="px-3 py-2">Expires</th>
                              <th className="px-3 py-2 text-right">Active</th>
                            </tr>
                          </thead>
                          <tbody>
                            {shareCodes.map((sc) => {
                              const statusLabel = {
                                active: '🟢 Active',
                                limit_reached: '⚪ Limit Reached',
                                expired: '🟠 Expired',
                                deactivated: '🔴 Deactivated',
                              }[sc.status] || sc.status;
                              return (
                                <tr key={sc.code} className="border-t" style={{ borderColor: '#D6D0C2' }}>
                                  <td className="px-3 py-2">
                                    <code className="px-2 py-1 rounded font-mono" style={{ backgroundColor: '#F3EEE4' }}>
                                      {sc.code}
                                    </code>
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap">{statusLabel}</td>
                                  <td className="px-3 py-2 whitespace-nowrap">{sc.uses}/{sc.max_uses}</td>
                                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(sc.created_at)}</td>
                                  <td className="px-3 py-2 whitespace-nowrap">{sc.expires_at ? formatDate(sc.expires_at) : '—'}</td>
                                  <td className="px-3 py-2 text-right">
                                    <Switch
                                      checked={sc.active}
                                      disabled={togglingShareCode === sc.code}
                                      onCheckedChange={(checked) => handleToggleShareCode(sc.code, checked)}
                                      data-testid={`sharecode-toggle-${sc.code}`}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Email Activity Tab */}
              {activeTab === 'emailActivity' && (
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3
                        className="text-xl font-semibold mb-1"
                        style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }}
                      >
                        Lifecycle Email Activity
                      </h3>
                      <p
                        className="text-sm"
                        style={{ color: '#51120A', opacity: 0.7, fontFamily: "'Bricolage Grotesque', sans-serif" }}
                      >
                        Showing the most recent 250 lifecycle emails. Opens are approximate because
                        some email apps block tracking or preload images. Clicks are the stronger
                        sign of engagement.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={fetchEmailActivity}
                      disabled={loadingEmailActivity}
                      style={{ color: '#51120A', borderColor: '#51120A' }}
                    >
                      {loadingEmailActivity ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      ['Sent', emailActivity.totals.sent],
                      ['Delivered', emailActivity.totals.delivered],
                      ['Opened', emailActivity.totals.opened],
                      ['Clicked', emailActivity.totals.clicked],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="rounded-lg border p-4"
                        style={{ borderColor: '#D6D0C2', backgroundColor: '#EEE7D8', color: '#51120A' }}
                      >
                        <div className="text-xs opacity-70">{label}</div>
                        <div className="text-2xl font-semibold">{value}</div>
                      </div>
                    ))}
                  </div>

                  {loadingEmailActivity && emailActivity.emails.length === 0 ? (
                    <p style={{ color: '#51120A' }}>Loading email activity...</p>
                  ) : emailActivity.emails.length === 0 ? (
                    <p
                      className="rounded-lg border p-5 text-sm"
                      style={{ color: '#51120A', borderColor: '#D6D0C2', backgroundColor: '#EEE7D8' }}
                    >
                      No lifecycle emails have been recorded yet.
                    </p>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border" style={{ borderColor: '#D6D0C2' }}>
                      <table
                        className="w-full text-sm"
                        style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: '#EEE7D8', textAlign: 'left' }}>
                            <th className="px-3 py-2">Recipient</th>
                            <th className="px-3 py-2">Email</th>
                            <th className="px-3 py-2">Sent</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2 text-center">Opens</th>
                            <th className="px-3 py-2 text-center">Clicks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emailActivity.emails.map((email, index) => {
                            const status = lifecycleEmailStatus(email);
                            return (
                              <tr
                                key={`${email.email}-${email.step_id}-${email.sent_at}-${index}`}
                                className="border-t"
                                style={{ borderColor: '#D6D0C2' }}
                              >
                                <td className="px-3 py-2">{email.email}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{lifecycleEmailName(email.step_id)}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{formatDate(email.sent_at)}</td>
                                <td className="px-3 py-2 font-semibold" style={{ color: status.color }}>
                                  {status.label}
                                </td>
                                <td className="px-3 py-2 text-center">{email.open_count}</td>
                                <td className="px-3 py-2 text-center">{email.click_count}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-8">
                  <section
                    className="rounded-lg border p-5"
                    style={{ borderColor: '#D6D0C2', backgroundColor: '#EEE7D8' }}
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <h3
                          className="text-lg font-semibold mb-1"
                          style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }}
                        >
                          Default Recipe Categories
                        </h3>
                        <p
                          className="text-sm"
                          style={{ color: '#51120A', opacity: 0.7, fontFamily: "'Bricolage Grotesque', sans-serif" }}
                        >
                          These starter categories are copied to new or untouched profiles.
                          Existing users keep their own saved category lists.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={addDefaultCategory}
                        disabled={loadingDefaultCategories}
                        className="shrink-0"
                        style={{ color: '#51120A', borderColor: '#51120A' }}
                      >
                        <Plus size={16} className="mr-2" />
                        Add category
                      </Button>
                    </div>

                    {loadingDefaultCategories ? (
                      <p style={{ color: '#51120A' }}>Loading defaults...</p>
                    ) : (
                      <div className="space-y-3">
                        {defaultCategories.length === 0 && (
                          <p
                            className="text-sm py-3"
                            style={{ color: '#51120A', opacity: 0.7 }}
                          >
                            No default categories. Add one to give new users a starter list.
                          </p>
                        )}
                        {defaultCategories.map((category, index) => (
                          <div
                            key={category.id}
                            className="flex items-center gap-2"
                            data-testid={`default-category-${index}`}
                          >
                            <Input
                              value={category.name}
                              aria-label={`Default category ${index + 1}`}
                              onChange={(event) => updateDefaultCategoryName(index, event.target.value)}
                              style={{
                                borderColor: '#D6D0C2',
                                backgroundColor: '#F3EEE4',
                                fontFamily: "'Bricolage Grotesque', sans-serif"
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              aria-label={`Move ${category.name || 'category'} up`}
                              disabled={index === 0}
                              onClick={() => moveDefaultCategory(index, -1)}
                              style={{ color: '#51120A', borderColor: '#D6D0C2' }}
                            >
                              <ArrowUp size={16} />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              aria-label={`Move ${category.name || 'category'} down`}
                              disabled={index === defaultCategories.length - 1}
                              onClick={() => moveDefaultCategory(index, 1)}
                              style={{ color: '#51120A', borderColor: '#D6D0C2' }}
                            >
                              <ArrowDown size={16} />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              aria-label={`Delete ${category.name || 'category'}`}
                              disabled={defaultCategories.length === 1}
                              onClick={() => removeDefaultCategory(index)}
                              style={{ color: '#51120A', borderColor: '#D6D0C2' }}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        ))}
                        <div className="flex justify-end pt-2">
                          <Button
                            type="button"
                            onClick={saveDefaultCategories}
                            disabled={savingDefaultCategories || defaultCategoriesVersion === null}
                            style={{
                              backgroundColor: '#51120A',
                              fontFamily: "'Bricolage Grotesque', sans-serif"
                            }}
                          >
                            {savingDefaultCategories ? 'Saving...' : 'Save default categories'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </section>

                  <section
                    className="rounded-lg border p-5"
                    style={{ borderColor: '#D6D0C2', backgroundColor: '#EEE7D8' }}
                  >
                    <h3
                      className="text-lg font-semibold mb-1"
                      style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }}
                    >
                      Welcome Email Backfill
                    </h3>
                    <p
                      className="text-sm mb-4"
                      style={{ color: '#51120A', opacity: 0.7, fontFamily: "'Bricolage Grotesque', sans-serif" }}
                    >
                      Find verified existing accounts with fewer than 10 recipes. Suppressed
                      and already-enrolled accounts are always excluded. Sending starts My Story
                      immediately and schedules the remaining Welcome reminders.
                    </p>

                    {welcomeBackfill && (
                      <div
                        className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4 rounded-md p-4"
                        style={{ backgroundColor: '#F3EEE4', color: '#51120A' }}
                      >
                        <div>
                          <div className="text-xs opacity-70">Eligible now</div>
                          <div className="text-xl font-semibold">{welcomeBackfill.remaining_eligible_count}</div>
                        </div>
                        <div>
                          <div className="text-xs opacity-70">Already enrolled</div>
                          <div className="text-xl font-semibold">{welcomeBackfill.already_enrolled_count}</div>
                        </div>
                        <div>
                          <div className="text-xs opacity-70">10+ recipes</div>
                          <div className="text-xl font-semibold">{welcomeBackfill.recipe_threshold_count}</div>
                        </div>
                        <div>
                          <div className="text-xs opacity-70">Suppressed</div>
                          <div className="text-xl font-semibold">{welcomeBackfill.suppressed_count}</div>
                        </div>
                        <div>
                          <div className="text-xs opacity-70">Unverified</div>
                          <div className="text-xl font-semibold">{welcomeBackfill.unverified_count}</div>
                        </div>
                        {welcomeBackfill.scheduled_count > 0 && (
                          <div>
                            <div className="text-xs opacity-70">Started this batch</div>
                            <div className="text-xl font-semibold">{welcomeBackfill.scheduled_count}</div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex flex-wrap justify-end gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => requestWelcomeBackfill(true)}
                        disabled={checkingWelcomeBackfill || sendingWelcomeBackfill}
                        style={{ color: '#51120A', borderColor: '#51120A' }}
                      >
                        {checkingWelcomeBackfill ? 'Checking...' : 'Check eligible accounts'}
                      </Button>
                      {welcomeBackfill?.remaining_eligible_count > 0 && (
                        <Button
                          type="button"
                          onClick={() => requestWelcomeBackfill(false)}
                          disabled={checkingWelcomeBackfill || sendingWelcomeBackfill}
                          style={{
                            backgroundColor: '#51120A',
                            fontFamily: "'Bricolage Grotesque', sans-serif"
                          }}
                        >
                          {sendingWelcomeBackfill
                            ? 'Starting sequences...'
                            : `Start next ${Math.min(25, welcomeBackfill.remaining_eligible_count)} sequences`}
                        </Button>
                      )}
                    </div>
                  </section>

                  <div>
                    <h3
                      className="text-lg font-semibold mb-2"
                      style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }}
                    >
                      Email Invitations
                    </h3>
                    <p
                      className="text-sm"
                      style={{ color: '#51120A', opacity: 0.7, fontFamily: "'Bricolage Grotesque', sans-serif" }}
                    >
                      Invitations are sent per beta user from the Beta Submissions tab. Each
                      invitation link expires 48 hours after it is sent; recipients with an
                      expired link can request a fresh one themselves directly from that link.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t" style={{ borderColor: '#D6D0C2' }}>
          <Button
            onClick={() => onOpenChange(false)}
            style={{ 
              backgroundColor: '#51120A',
              fontFamily: "'Bricolage Grotesque', sans-serif"
            }}
          >
            Close
          </Button>
        </div>
      </DialogContent>

      {/* Add Manual Beta User Dialog */}
      <Dialog open={showAddBetaUser} onOpenChange={setShowAddBetaUser}>
        <DialogContent style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif', serif", fontSize: '24px', color: '#51120A' }}>
              Add Manual Beta User
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Email *
              </Label>
              <Input
                type="email"
                value={newBetaUser.email}
                onChange={(e) => setNewBetaUser({ ...newBetaUser, email: e.target.value })}
                placeholder="email@example.com"
                style={{ 
                  borderColor: '#D6D0C2',
                  backgroundColor: 'transparent',
                  fontFamily: "'Bricolage Grotesque', sans-serif"
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  First Name *
                </Label>
                <Input
                  value={newBetaUser.first_name}
                  onChange={(e) => setNewBetaUser({ ...newBetaUser, first_name: e.target.value })}
                  placeholder="First name"
                  style={{ 
                    borderColor: '#D6D0C2',
                    backgroundColor: 'transparent',
                    fontFamily: "'Bricolage Grotesque', sans-serif"
                  }}
                />
              </div>

              <div>
                <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  Last Name
                </Label>
                <Input
                  value={newBetaUser.last_name}
                  onChange={(e) => setNewBetaUser({ ...newBetaUser, last_name: e.target.value })}
                  placeholder="Last name"
                  style={{ 
                    borderColor: '#D6D0C2',
                    backgroundColor: 'transparent',
                    fontFamily: "'Bricolage Grotesque', sans-serif"
                  }}
                />
              </div>
            </div>

            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Category
              </Label>
              <select
                value={newBetaUser.category}
                onChange={(e) => setNewBetaUser({ ...newBetaUser, category: e.target.value })}
                className="w-full px-3 py-2 rounded"
                style={{ 
                  borderColor: '#D6D0C2',
                  border: '1px solid',
                  backgroundColor: 'transparent',
                  color: '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif"
                }}
              >
                <option value="Friend">Friend</option>
                <option value="Cold Lead">Cold Lead</option>
              </select>
            </div>

            <div>
              <Label style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Notes
              </Label>
              <textarea
                value={newBetaUser.notes}
                onChange={(e) => setNewBetaUser({ ...newBetaUser, notes: e.target.value })}
                placeholder="Add notes about this person..."
                className="w-full p-2 rounded"
                style={{ 
                  borderColor: '#D6D0C2',
                  border: '1px solid',
                  backgroundColor: 'transparent',
                  color: '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  minHeight: '80px'
                }}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddBetaUser(false);
                setNewBetaUser({
                  email: '',
                  first_name: '',
                  last_name: '',
                  category: 'Friend',
                  notes: ''
                });
              }}
              style={{ 
                borderColor: '#51120A',
                color: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddManualBetaUser}
              style={{ 
                backgroundColor: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
            >
              Add User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog.open} onOpenChange={(open) => setDeleteConfirmDialog({ open, submission: null })}>
        <DialogContent style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif', serif", fontSize: '24px', color: '#51120A' }}>
              Delete Beta Submission
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              Are you sure you want to delete the submission from{' '}
              <strong>
                {deleteConfirmDialog.submission?.first_name} {deleteConfirmDialog.submission?.last_name}
              </strong>
              ? This action cannot be undone.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmDialog({ open: false, submission: null })}
              style={{ 
                borderColor: '#51120A',
                color: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteBetaSubmission}
              style={{ 
                backgroundColor: '#AE8890',
                borderColor: '#AE8890',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default OwnerDashboard;
